import { z } from 'zod';
import {
  generationClassificationSchema,
  generationSourceInventorySchema,
  pageCompositionJsonSchema,
} from '../lp-generation/schema';

export const siteStatusSchema = z.enum(['draft', 'published']);

export const siteThemeTokensSchema = z.object({
  text: z.string().min(1),
  textMuted: z.string().min(1),
  background: z.string().min(1),
  surface: z.string().min(1),
  surfaceMuted: z.string().min(1),
  border: z.string().min(1),
  primary: z.string().min(1),
  secondary: z.string().min(1),
});

export type SiteThemeTokens = z.infer<typeof siteThemeTokensSchema>;

export const siteThemeSchema = z.object({
  fontFamily: z.literal('Noto Sans JP'),
  preset: z.string().min(1),
  tokens: siteThemeTokensSchema,
  // AI が変更不要時に null を返すため nullable も許容する。
  // mergePreservedFields で null/undefined は元ドキュメントの値にフォールバックされる。
  presentation: z.object({
    archetypeId: z.string().min(1),
    sectionHeadingStyle: z.enum(['center_bar', 'left_rule', 'center_panel']),
    cardStyle: z.enum(['outline', 'surface', 'tint', 'elevated']),
    cardShadow: z.enum(['none', 'soft', 'medium', 'strong']).optional(),
    borderEmphasis: z.enum(['none', 'soft', 'normal', 'strong']).optional(),
    cardRadius: z.enum(['sharp', 'soft', 'rounded', 'extra_rounded']).optional(),
    spacingScale: z.enum(['compact', 'normal', 'spacious']).optional(),
    heroLayoutStyle: z.enum(['authority_band', 'story_split', 'product_frame', 'editorial_centered']),
    proofLayoutStyle: z.enum(['grid', 'band', 'pill']),
    reviewLayoutStyle: z.enum(['cards', 'speech', 'editorial']),
    ctaLayoutStyle: z.enum(['panel', 'band', 'contrast']),
  }).nullable().optional(),
});

export type SiteTheme = z.infer<typeof siteThemeSchema>;
export type SiteThemePresentation = NonNullable<SiteTheme['presentation']>;
export type SiteThemeCardShadow = NonNullable<SiteThemePresentation['cardShadow']>;
export type SiteThemeBorderEmphasis = NonNullable<SiteThemePresentation['borderEmphasis']>;
export type SiteThemeCardRadius = NonNullable<SiteThemePresentation['cardRadius']>;
export type SiteThemeSpacingScale = NonNullable<SiteThemePresentation['spacingScale']>;

const siteFeatureItemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});

const siteContentItemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});

const siteStepItemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});

const siteFaqItemSchema = z.object({
  id: z.string().min(1),
  question: z.string().min(1),
  answer: z.string().min(1),
});

const siteProofItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  value: z.string().min(1),
});

const siteReviewItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  role: z.string().optional().or(z.literal('')).default(''),
  comment: z.string().min(1),
});

const pricingAccentColorSchema = z.enum(['warm', 'cool', 'soft', 'neutral']);
export type PricingAccentColor = z.infer<typeof pricingAccentColorSchema>;

const sitePricingItemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  price: z.string().min(1),
  body: z.string().optional().or(z.literal('')).default(''),
  accentColor: pricingAccentColorSchema.optional(),
});

const siteFooterLinkSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  href: z.string().optional().or(z.literal('')).default(''),
  current: z.boolean().default(false),
});

const siteSectionPresentationSchema = z.object({
  templateId: z.string().min(1).optional(),
  semanticType: z.string().min(1).optional(),
});

const siteOptionalSectionImageSchema = z.object({
  imageUrl: z.string().url().optional().or(z.literal('')).optional(),
  imageAlt: z.string().optional().or(z.literal('')).optional(),
  imageAssetId: z.string().optional().or(z.literal('')).optional(),
});

const sitePrimarySectionImageSchema = siteOptionalSectionImageSchema.extend({
  imageUrl: z.string().url().optional().or(z.literal('')).default(''),
  imageAlt: z.string().optional().or(z.literal('')).default(''),
  imageAssetId: z.string().optional().or(z.literal('')).optional(),
});

const informativeSectionTypeSchema = z.enum([
  'problem',
  'solution',
  'benefit',
  'feature_list',
  'reasons',
  'use_cases',
  'comparison',
  'people',
  'message',
  'target_audience',
  'overview',
  'speaker',
  'instructor',
  'work_style',
  'course_or_curriculum',
  'offer',
  'access',
]);

export const heroSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('hero'),
  eyebrow: z.string().optional().default(''),
  headline: z.string().min(1),
  subhead: z.string().min(1),
  ctaLabel: z.string().min(1),
  secondaryCtaLabel: z.string().optional(),
  phoneNumber: z.string().optional(),
}).merge(siteSectionPresentationSchema).merge(sitePrimarySectionImageSchema);

export const featureListSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('feature-list'),
  title: z.string().min(1),
  items: z.array(siteFeatureItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const informativeSectionSchema = z.object({
  id: z.string().min(1),
  type: informativeSectionTypeSchema,
  title: z.string().min(1),
  items: z.array(siteContentItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const servicesSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('services'),
  title: z.string().min(1),
  items: z.array(siteContentItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const featuresSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('features'),
  title: z.string().min(1),
  items: z.array(siteContentItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const proofSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('proof'),
  title: z.string().min(1),
  items: z.array(siteProofItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const reviewSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('review'),
  title: z.string().min(1),
  items: z.array(siteReviewItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const pricingSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('pricing'),
  title: z.string().min(1),
  items: z.array(sitePricingItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const stepsSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('steps'),
  title: z.string().min(1),
  items: z.array(siteStepItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const faqSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('faq'),
  title: z.string().min(1),
  items: z.array(siteFaqItemSchema).min(1),
}).merge(siteSectionPresentationSchema).merge(siteOptionalSectionImageSchema);

export const ctaSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('cta'),
  headline: z.string().min(1),
  body: z.string().min(1),
  ctaLabel: z.string().min(1),
}).merge(siteSectionPresentationSchema).merge(sitePrimarySectionImageSchema);

export const footerSectionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('footer'),
  siteName: z.string().min(1),
  companyName: z.string().optional().or(z.literal('')).default(''),
  description: z.string().optional().or(z.literal('')).default(''),
  goalLabel: z.string().optional().or(z.literal('')).default(''),
  pageColumnTitle: z.string().optional().or(z.literal('')).default('ページ'),
  legalColumnTitle: z.string().optional().or(z.literal('')).default('法務'),
  supportColumnTitle: z.string().optional().or(z.literal('')).default('サポート'),
  supportFallbackMessage: z.string().optional().or(z.literal('')).default(''),
  pageLinks: z.array(siteFooterLinkSchema).default([]),
  legalLinks: z.array(siteFooterLinkSchema).default([]),
  supportLinks: z.array(siteFooterLinkSchema).default([]),
}).merge(siteSectionPresentationSchema);

export const siteSectionSchema = z.discriminatedUnion('type', [
  heroSectionSchema,
  featureListSectionSchema,
  informativeSectionSchema,
  servicesSectionSchema,
  featuresSectionSchema,
  proofSectionSchema,
  reviewSectionSchema,
  pricingSectionSchema,
  stepsSectionSchema,
  faqSectionSchema,
  ctaSectionSchema,
  footerSectionSchema,
]);

export type SiteSection = z.infer<typeof siteSectionSchema>;

export const sitePagePathSchema = z
  .string()
  .min(1)
  .regex(/^\/[a-z0-9-]*$/u, 'path は /about のような形式で指定してください。');

export const sitePageSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  path: sitePagePathSchema,
  sections: z.array(siteSectionSchema).min(1),
});

export type SitePage = z.infer<typeof sitePageSchema>;

export const hearingProfileSchema = z.object({
  serviceDescription: z.string().default(''),
  targetCustomers: z.string().default(''),
  strengths: z.string().default(''),
  customerPain: z.string().default(''),
  proofPoints: z.string().default(''),
  desiredFlow: z.string().default(''),
  desiredAction: z.string().default(''),
  offerDetails: z.string().default(''),
  atmosphere: z.string().default(''),
  hasTestimonials: z.boolean().default(false),
  hasPricing: z.boolean().default(false),
  additionalInfo: z.string().default(''),
}).optional();

export type HearingProfile = z.infer<typeof hearingProfileSchema>;

export const seoAdvancedSchema = z.object({
  canonicalUrl: z.string().optional().or(z.literal('')).default(''),
  robotsMeta: z.string().optional().or(z.literal('')).default(''),
  structuredDataJson: z.string().optional().or(z.literal('')).default(''),
  ogType: z.string().optional().or(z.literal('')).default('website'),
  ogLocale: z.string().optional().or(z.literal('')).default('ja_JP'),
  twitterCard: z.string().optional().or(z.literal('')).default('summary_large_image'),
}).optional();

export type SeoAdvanced = z.infer<typeof seoAdvancedSchema>;

const siteDocumentPipelineContextSchema = z.object({
  normalized: z.unknown().optional(),
  classification: generationClassificationSchema.optional(),
  sourceInventory: generationSourceInventorySchema.optional(),
  pageComposition: pageCompositionJsonSchema.optional(),
}).optional();

export const siteDocumentMetadataSchema = z.object({
  goal: z.string().min(1),
  tone: z.string().min(1),
  companyName: z.string().optional().or(z.literal('')).default(''),
  ctaText: z.string().optional().or(z.literal('')).default(''),
  referenceUrl: z.string().url().optional().or(z.literal('')).default(''),
  pageTitle: z.string().optional().or(z.literal('')).default(''),
  pageDescription: z.string().optional().or(z.literal('')).default(''),
  ogImageUrl: z.string().url().optional().or(z.literal('')).default(''),
  faviconUrl: z.string().url().optional().or(z.literal('')).default(''),
  hearingProfile: hearingProfileSchema,
  seoAdvanced: seoAdvancedSchema,
  pipelineContext: siteDocumentPipelineContextSchema,
});

export type SiteDocumentMetadata = z.infer<typeof siteDocumentMetadataSchema>;

export const siteDocumentSchema = z.object({
  version: z.number().int().positive(),
  siteName: z.string().min(1),
  metadata: siteDocumentMetadataSchema,
  theme: siteThemeSchema,
  pages: z.array(sitePageSchema).min(1),
  updatedAt: z.string().optional(),
});

export type SiteDocument = z.infer<typeof siteDocumentSchema>;

const sitePublicationTagConfigSchema = z.object({
  gtmId: z.string().optional().or(z.literal('')).default(''),
  ga4Id: z.string().optional().or(z.literal('')).default(''),
  metaPixelId: z.string().optional().or(z.literal('')).default(''),
});

export type SitePublicationTagConfig = z.infer<typeof sitePublicationTagConfigSchema>;

const sitePublicationFormFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(['text', 'email', 'textarea']),
  required: z.boolean(),
  placeholder: z.string().optional().or(z.literal('')).default(''),
});

export type SitePublicationFormField = z.infer<typeof sitePublicationFormFieldSchema>;

const sitePublicationFormConfigSchema = z.object({
  enabled: z.boolean().default(false),
  successMessage: z.string().min(1).default('お問い合わせありがとうございます。'),
  privacyPolicyUrl: z.string().optional().or(z.literal('')).default(''),
  rateLimitSeconds: z.number().int().min(10).max(3600).default(30),
  fields: z.array(sitePublicationFormFieldSchema).default([]),
});

export type SitePublicationFormConfig = z.infer<typeof sitePublicationFormConfigSchema>;

export const sitePublicationPublicConfigSchema = z.object({
  tags: sitePublicationTagConfigSchema.default({
    gtmId: '',
    ga4Id: '',
    metaPixelId: '',
  }),
  form: sitePublicationFormConfigSchema.default({
    enabled: false,
    successMessage: 'お問い合わせありがとうございます。',
    privacyPolicyUrl: '',
    rateLimitSeconds: 30,
    fields: [],
  }),
});

export type SitePublicationPublicConfig = z.infer<typeof sitePublicationPublicConfigSchema>;

export const sitePublicationSchema = z.object({
  site_id: z.string(),
  version: z.number().int().positive(),
  document: siteDocumentSchema,
  workspace_version: z.number().int().positive().nullable().optional(),
  public_config: sitePublicationPublicConfigSchema.default({
    tags: {
      gtmId: '',
      ga4Id: '',
      metaPixelId: '',
    },
    form: {
      enabled: false,
      successMessage: 'お問い合わせありがとうございます。',
      privacyPolicyUrl: '',
      rateLimitSeconds: 30,
      fields: [],
    },
  }),
  published_at: z.string(),
  updated_at: z.string(),
});

export type SitePublication = z.infer<typeof sitePublicationSchema>;

const siteSummaryRowSchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  name: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  custom_domain: z.string().nullable().optional(),
  preview_token: z.string().nullable().optional(),
  status: siteStatusSchema,
  created_at: z.string(),
  updated_at: z.string().optional(),
});

export const siteSummarySchema = siteSummaryRowSchema.transform((row) => ({
  id: row.id,
  slug: row.slug,
  name: row.name ?? row.title ?? null,
  custom_domain: row.custom_domain ?? null,
  preview_token: row.preview_token ?? null,
  status: row.status,
  created_at: row.created_at,
  updated_at: row.updated_at,
}));

export type SiteSummary = z.infer<typeof siteSummarySchema>;

export const createSiteDraftInputSchema = z.object({
  siteName: z.string().min(1),
  goal: z.string().min(1),
  tone: z.string().min(1),
  companyName: z.string().optional(),
  ctaText: z.string().optional(),
  referenceUrl: z.string().url().optional().or(z.literal('')),
  mode: z.enum(['template', 'default']).optional(),
});

export type CreateSiteDraftInput = z.infer<typeof createSiteDraftInputSchema>;

export const updateSiteSettingsInputSchema = z
  .object({
    slug: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u, 'slug は英小文字・数字・ハイフンのみ使用できます。')
      .optional()
      .or(z.literal('')),
    status: siteStatusSchema,
  })
  .superRefine((value, context) => {
    if (value.status === 'published' && !value.slug) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: '公開するには slug を設定してください。',
        path: ['slug'],
      });
    }
  });

export type UpdateSiteSettingsInput = z.infer<typeof updateSiteSettingsInputSchema>;

export const siteRevisionSchema = z.object({
  id: z.string(),
  site_id: z.string(),
  version: z.number().int().positive(),
  workspace_version: z.number().int().positive().nullable().optional(),
  summary: z.string().min(1),
  created_at: z.string(),
});

export type SiteRevision = z.infer<typeof siteRevisionSchema>;
